// Главный экран — обзор активных скважин.
// Портировано из design_ref/src/Dashboard.jsx.

import { For } from 'solid-js'
import type { Component } from 'solid-js'
import { Icon } from './Icons'
import { Sparkline, series } from './Charts'
import { WELLS, ACTIVITY, TASKS } from './data'

type DashboardProps = {
  onOpenWell: (id: string) => void
  onOpenView: (v: string) => void
}

const STATS = [
  { label: 'Активных скважин',    value: '6',     delta: '+1 за неделю', up: true,  down: false, neutral: false },
  { label: 'Суммарная проходка',  value: '248 м', delta: 'за смену',     up: false, down: false, neutral: true  },
  { label: 'Открытых задач',      value: '14',    delta: '3 срочных',    up: false, down: true,  neutral: false },
  { label: 'Инцидентов',          value: '2',     delta: '1 активный',   up: false, down: true,  neutral: false },
]

const STATUS_BADGE: Record<string, [string, string]> = {
  active: ['ok',   'В работе'],
  warn:   ['warn', 'Внимание'],
  err:    ['err',  'Авария'],
  idle:   ['',     'Ожидание'],
}

export const Dashboard: Component<DashboardProps> = (props) => {
  return (
    <div class="page">
      <div class="page-header">
        <div>
          <h1 class="page-title">Главная</h1>
          <p class="page-subtitle">Сводка по всем активным скважинам · 28 апреля 2026, 14:32 МСК</p>
        </div>
        <div class="row">
          <div class="tabs">
            <div class="tab active">Все</div>
            <div class="tab">Мои</div>
            <div class="tab">Избранное</div>
          </div>
        </div>
      </div>

      <div class="grid-4" style={{ 'margin-bottom': 'var(--sp-7)' }}>
        <For each={STATS}>
          {(s) => (
            <div class="stat">
              <div class="stat-label">{s.label}</div>
              <div class="stat-value">{s.value}</div>
              <div class={'stat-delta' + (s.up ? ' up' : s.down ? ' down' : '')}>{s.delta}</div>
            </div>
          )}
        </For>
      </div>

      <div class="section-head">
        <h2>Скважины</h2>
        <div class="row">
          <div class="tabs">
            <div class="tab active"><Icon name="grid" size={12} /></div>
            <div class="tab"><Icon name="list" size={12} /></div>
          </div>
        </div>
      </div>

      <div class="well-grid" style={{ 'margin-bottom': 'var(--sp-7)' }}>
        <For each={WELLS.slice(0, 6)}>
          {(w, idx) => {
            const progress = Math.round((w.depth / w.target) * 100)
            const sb = STATUS_BADGE[w.status]!
            return (
              <div class="well-card" onClick={() => props.onOpenWell(w.id)}>
                <div class="well-card-head">
                  <div>
                    <div class="well-card-id mono">{w.id}</div>
                    <h3 class="well-card-name">{w.name}</h3>
                    <div class="muted text-xs" style={{ 'margin-top': '4px' }}>{w.field} · {w.op}</div>
                  </div>
                  <span class={'badge ' + sb[0]}><span class={'dot ' + (sb[0] || 'idle')} />{sb[1]}</span>
                </div>

                <Sparkline
                  data={series(idx() + 1, 40, 100, 14)}
                  color={w.status === 'err' ? 'var(--status-err)' : w.status === 'warn' ? 'var(--status-warn)' : 'var(--accent)'}
                />

                <div class="well-card-stats">
                  <div>
                    <div class="well-card-stat-label">Глубина</div>
                    <div class="well-card-stat-value">{w.depth} м</div>
                  </div>
                  <div>
                    <div class="well-card-stat-label">Прогресс</div>
                    <div class="well-card-stat-value">{progress}%</div>
                  </div>
                  <div>
                    <div class="well-card-stat-label">МСП</div>
                    <div class="well-card-stat-value">{w.rop || '—'}</div>
                  </div>
                </div>
              </div>
            )
          }}
        </For>
      </div>

      <div class="grid-2">
        <div class="block">
          <div class="block-head">
            <div class="block-title">
              <Icon name="activity" size={12} style={{ 'margin-right': '6px', color: 'var(--text-tertiary)' }} />Активность
            </div>
            <button class="btn btn-ghost text-xs">Открыть журнал</button>
          </div>
          <div class="block-body" style={{ 'padding-top': 0, 'padding-bottom': 0 }}>
            <div class="activity">
              <For each={ACTIVITY}>
                {(a) => (
                  <div class="activity-row">
                    <div class="activity-time mono">{a.time}</div>
                    <div class="activity-body">
                      <strong>{a.who}</strong> {a.what} <span style={{ color: 'var(--accent)' }}>{a.target}</span>
                      <span class="muted"> · {a.well}</span>
                    </div>
                  </div>
                )}
              </For>
            </div>
          </div>
        </div>

        <div class="block">
          <div class="block-head">
            <div class="block-title">
              <Icon name="bug" size={12} style={{ 'margin-right': '6px', color: 'var(--text-tertiary)' }} />Задачи
            </div>
            <button class="btn btn-ghost text-xs" onClick={() => props.onOpenView('tasks')}>Все задачи</button>
          </div>
          <div class="block-body tight">
            <ul class="task-list">
              <For each={TASKS}>
                {(t) => (
                  <li class={'task' + (t.done ? ' done' : '')}>
                    <input type="checkbox" checked={t.done} />
                    <div style={{ flex: 1, 'min-width': 0 }}>
                      <div class="task-text">{t.title}</div>
                      <div class="task-meta">
                        <span class="mono">{t.id}</span>
                        <span>·</span>
                        <span class="mono">{t.well}</span>
                        <span>·</span>
                        <span>{t.due}</span>
                        {t.priority === 'high' && <span class="badge err" style={{ 'margin-left': '4px' }}>срочно</span>}
                      </div>
                    </div>
                  </li>
                )}
              </For>
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}
